import { useSortable } from "@dnd-kit/sortable";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import TextareaAutosize from "react-autosize-textarea";
import { FileService } from "src/utils/file.service";

import { useSelector } from "react-redux";
import { useDroppable } from '@dnd-kit/core';


import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import Slider from "src/components/slider";

import { Assessment, Course, Lesson, Section } from "src/types/coaching/courses";
import Reveal from "src/components/animation";
import Text from "src/components/text";

import { rearrangeEntitiesInCourse, updateCourseSectionProperty } from "src/redux/actions/coaching/courses";

import _ from "lodash";
import cx from "classnames";
import styles from "./cards.module.scss";
import { useSpring, animated } from "@react-spring/web";
import { getIndexForContent } from "../../helper";

export interface LessonCardProps {
  lesson: Lesson;
  index: number;
  lessonIndex: number | string;
  className?: string;
  active: boolean;
  mode?: 'view' | 'edit';
  readonly?: boolean;
  children?: React.ReactNode;

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (parent: string | null, index: string) => void;
}

export const LessonCard: React.FC<LessonCardProps> = ({ lesson, index, lessonIndex, mode, className, active, readonly, children, onClick, onRemove }) => {
  const editMode = mode === 'edit';
  const content = _.get(lesson, 'content');
  const name = _.get(lesson, 'name') || "Untitled Lesson";

  const fileType = useMemo(() => {
    return FileService.getFileType(_.get(content, "metadata", ""));
  }, [content]);

  const readTime = useMemo(() => {
    const videoDuration = _.get(content, "metadata.video_duration", 0);
    const readDuration = _.get(content, "metadata.read_time", 0);

    const duration = videoDuration || readDuration;
    if (duration === 0) {
      return null;
    }

    return FileService.formatDuration(videoDuration || readDuration);
  }, [content]);

  const errors = useSelector((state: any) => {
    let errorList = _.get(state.courses, 'edit.errors') || [];
    errorList = _.filter(errorList, { type: 'lesson', uuid: lesson.uuid });
    return _.size(errorList);
  }, _.isEqual);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: lesson!.uuid || 'new-lesson',
    disabled: readonly,
    data: {
      entity: lesson,
      index,
      parent: lesson.parent || null,
      type: 'lesson'
    }
  });


  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(lesson.parent, lesson.uuid);
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  return (
    <div className={cx(styles.entity, styles.lesson, className, {
      [styles.readonly]: !editMode,
      [styles.errors]: errors > 0,
      [styles.active]: active,
      [styles.dragging]: isDragging,
    })} ref={setNodeRef} {...listeners} {...attributes} onClick={handleClick}>
      {editMode &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }

      {(!lesson.parent || mode === 'view') &&
        <div className={styles.index}>{lessonIndex} /</div>
      }
      <div className={styles.entityDetail}>
        <Thumbnail className={styles.icon} media={_.get(content, "icon")} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{fileType}</span>
            {readTime &&
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{readTime}</span>
              </>
            }
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {children}
      </div>

      {editMode &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }
      {errors > 0 &&
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      }
    </div>
  );
}

export interface AssessmentCardProps {
  assessment: Assessment;
  assessmentIndex: number | string;
  index: number;
  mode?: 'view' | 'edit';
  className?: string;
  active: boolean;
  score?: string | number | null;
  readonly?: boolean;
  children?: React.ReactNode;

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (parent: string | null, index: string) => void;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({ assessment, index, mode, className, active, score, readonly, children, onClick, onRemove, assessmentIndex }) => {
  const editMode = mode === 'edit';

  const errors = useSelector((state: any) => {
    let errorList = _.get(state.courses, 'edit.errors') || [];
    errorList = _.filter(errorList, { type: 'assessment', uuid: assessment.uuid });
    return _.size(errorList);
  }, _.isEqual);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: assessment!.uuid || 'new-assessment',
    disabled: !editMode,
    data: {
      entity: assessment,
      index,
      parent: assessment.parent || null,
      type: 'assessment'
    }
  });


  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(assessment.parent, assessment.uuid);
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editMode && e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  return (
    <div className={cx(styles.entity, styles.assessment, className, {
      [styles.readonly]: !editMode,
      [styles.errors]: errors > 0,
      [styles.active]: active,
      [styles.dragging]: isDragging
    })} ref={setNodeRef} {...listeners} {...attributes} onClick={handleClick}>
      {editMode &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }

      {(!assessment.parent || mode === 'view') &&
        <div className={styles.index}>{assessmentIndex} /</div>
      }

      <div className={styles.entityDetail}>
        <Thumbnail className={styles.icon} media={null} defaultImage="/images/coaching/assessment.svg" />
        <div className={styles.details}>
          <Text className={styles.name} text={_.get(assessment, 'name') || `Untitled Assessment`} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>Assessment</span>
            <span className={styles.separator}>&nbsp;</span>
            <span className={styles.value}>{_.size(_.get(assessment, 'questions'))} Questions</span>
            {score && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>score: </span><span className={styles.value}>{score}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {children}
      </div>

      {editMode &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }
      {errors > 0 &&
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      }
    </div>
  );
}

export interface SectionCardProps {
  mode?: 'view' | 'edit';
  section: Section;
  index: number;
  sectionIndex: number;
  className?: string;
  active?: boolean;
  readonly?: boolean;
  contentChildIndex?: number;
  children?: React.ReactNode;
  onSimulationClick?: (content: any) => void;

  dragIndicator?: {
    index: number;
    parent: string | null;
    before: boolean;
  };
  viewReport?: boolean;
  // eslint-disable-next-line no-unused-vars
  handleViewReport?: (content: any) => void;

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number, section: number) => void;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (parent: string | null, id: string) => void;

  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;

  renderChildrenForContent?: (content: any) => React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  mode,
  section,
  index,
  sectionIndex,
  contentChildIndex,
  className,
  readonly,
  dragIndicator,
  children,
  onClick,
  onRemove,
  onAddLessonClicked,
  onAddAssessmentClicked,
  viewReport,
  handleViewReport,
  renderChildrenForContent,
  onSimulationClick
}) => {

  const dispatch = useDispatch();

  const course: Course = useSelector((state: any) => {
    return _.get(state.courses, `edit.item`) as Course;
  });
  const editMode = mode === 'edit';
  const [showDeleteSlider, setShowDeleteSlider] = useState(false);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, {
      type: "section",
      uuid: section.uuid,
      property: "name",
    });
  });

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, {
      type: "section",
      uuid: section.uuid,
      property: "description",
    });
  });

  const errors = useSelector((state: any) => {
    let errorList = _.get(state.courses, "edit.errors") || [];
    errorList = _.filter(errorList, { type: "assessment", uuid: section.uuid });
    return _.size(errorList);
  }, _.isEqual);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: section!.uuid || "new-lesson",
    disabled: readonly,
    data: {
      entity: section,
      index: sectionIndex,
      parent: null,
      type: "section",
    },
  });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editMode && e) {
      e.stopPropagation();
    }
    onClick?.(index, 0);
  };

  const handleContentClick = (index: number, contentIndex: number) => {
    if (onClick) {
      onClick(index, contentIndex);
    }
  }

  const { setNodeRef: setSectionDroppableRef } = useDroppable({
    id: `section-${section.uuid}`,
    data: {
      entityType: "section",
      entity: section,
      parent: section.uuid,
      index: 0,
    },
  })

  const handleNameChange = (e: any) => {
    handlePropertyChange("name", e.target.value);
  };

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updateCourseSectionProperty({
        course: course.id!,
        section: section.uuid,
        property,
        value,
      }),
    );
  };

  const handleDescriptionChange = (e: any) => {
    const content = e.target.value;
    handlePropertyChange("description", content);
  };

  const handleMoveUp = () => {
    const prevIndex = index - 1;
    if (prevIndex < 0) {
      return;
    }

    const prevContent = course.contents ? course.contents[prevIndex] : null;
    if (!prevContent) {
      return;
    }

    dispatch(rearrangeEntitiesInCourse({
      id: course.id!,
      source: {
        id: section.uuid,
        entityType: section.entityType,
        parent: null,
      },
      target: {
        id: prevContent.uuid,
        entityType: prevContent.entityType,
        parent: null,
      }
    }));
  };

  const handleMoveDown = () => {
    const nextIndex = index + 1;
    if (nextIndex >= (course.contents?.length || 0)) {
      return;
    }

    const nextContent = course.contents ? course.contents[nextIndex] : null;
    if (!nextContent) {
      return;
    }

    dispatch(rearrangeEntitiesInCourse({
      id: course.id!,
      source: {
        id: section.uuid,
        entityType: section.entityType,
        parent: null,
      },
      target: {
        id: nextContent.uuid,
        entityType: nextContent.entityType,
        parent: null,
      }
    }));
  };

  const getLessonIndexForContent = (sectionIndex) => {
    return `${index + 1}.${sectionIndex + 1}`;
  };


  const handleDeleteClick = () => {
    if (section.contents && section.contents.length > 0) {
      // if section has content, show the slider
      setShowDeleteSlider(true);
    } else {
      // otherwise, delete immediately
      onRemove?.(null, section.uuid);
    }
  };


  return (
    <div
      ref={setNodeRef}
      className={cx(styles.sectionEntity, className, {
        [styles.readonly]: readonly,
        [styles.errors]: errors > 0,
        [styles.sectionWrapper]: viewReport
      })}
      onClick={handleClick}
    >
      <div className={styles.header}>
        {editMode && (
          <i
            className={cx(styles.dragHandle, "paperflite-drag")}
            {...listeners}
            {...attributes}
          ></i>
        )}
        <div className={styles.headerContent}>
          <div className={styles.nameWrapper}>
            <div className={styles.sectionIndex}>{sectionIndex} /</div>
            <div className={styles.nameContainer}>
              {editMode ? (
                <TextareaAutosize
                  className={styles.name}
                  value={section.name || ""}
                  rows={1}
                  maxRows={6}
                  placeholder="Enter section name..."
                  onChange={handleNameChange}
                  onPointerEnterCapture={() => { }}
                  onPointerLeaveCapture={() => { }}
                />
              ) : (
                <Text className={styles.name} text={section?.name || ""} lines={1} />
              )}
              <Reveal show={nameError != null}>
                <div className={styles.error}>
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <span>{nameError?.message}</span>
                </div>
              </Reveal>
            </div>
          </div>

          <div className={styles.right}>
            {children}
          </div>
          {editMode && (
            <div className={styles.actions}>
              <div className={styles.group}>
                <button className={styles.action} onClick={handleMoveUp} disabled={index === 0}>
                  <i className={cx("paperflite-up-arrow")}></i>
                </button>
                <button className={styles.action} onClick={handleMoveDown} disabled={index === (course.contents?.length || 1) - 1}>
                  <i className={cx("paperflite-dropdown")}></i>
                </button>
              </div>

              <div className={styles.divider}></div>

              <button
                className={styles.action}
                onClick={handleDeleteClick}
              >
                <i className="paperflite-delete"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        {editMode && (
          <TextareaAutosize
            className={cx(styles.description, {
              [styles.editable]: !readonly,
            })}
            rows={2}
            maxRows={6}
            value={section.description || ""}
            placeholder={"Add your description here..."}
            spellCheck={true}
            onChange={handleDescriptionChange}
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
          />
        )}

        {section.description && mode === 'view' && (
          <p className={styles.description}>{section.description}</p>
        )}

        <Reveal show={descriptionError != null}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{descriptionError?.message}</span>
          </div>
        </Reveal>
      </div>

      <div className={styles.body}>
        {_.size(section.contents) === 0 ? (
          <div ref={setSectionDroppableRef} className={styles.emptyBody}>
            {dragIndicator?.index === 0 ? (
              <DropIndicator key={`drop-indicator-${dragIndicator?.index}-before`} />
            ) : (
              _.times(2, (i) => (
                <ContentEmptyCard key={i} index={i} />
              ))
            )}
          </div>
        ) : (
          <>
            {section.contents.map((content, contentIdx) => {
              return (
                <React.Fragment key={content.uuid}>
                  {dragIndicator?.index === contentIdx && dragIndicator?.before &&
                    <DropIndicator key={`drop-indicator-${dragIndicator?.index}-before`} />
                  }

                  {content.entityType === "lesson" && (
                    <div className={styles.cardWrapper}>
                      <LessonCard
                        key={content.uuid}
                        mode={mode}
                        lesson={content as Lesson}
                        lessonIndex={getLessonIndexForContent(contentIdx)}
                        index={contentIdx}
                        active={contentIdx === contentChildIndex}
                        onClick={() => handleContentClick(index, contentIdx)}
                        onRemove={onRemove}
                      >
                        {renderChildrenForContent?.(content)}
                      </LessonCard>
                    </div>
                  )}

                  {content.entityType === "assessment" && (
                    <AssessmentCard
                      key={content.uuid}
                      index={contentIdx}
                      assessmentIndex={getLessonIndexForContent(contentIdx)}
                      assessment={content as Assessment}
                      mode={mode}
                      active={contentIdx === contentChildIndex}
                      onClick={() => {
                        if (viewReport) {
                          handleViewReport?.(content);
                          return;
                        }
                        handleContentClick(index, contentIdx);
                      }}
                      onRemove={onRemove}
                    >
                      {viewReport && (
                        <div className={styles.reportView}>
                          <i className={cx(styles.arrowUp, "paperflite-arrow-up")} />
                        </div>
                      )}
                      {renderChildrenForContent?.(content)}
                    </AssessmentCard>
                  )}
                  {(content.entityType === "podcast") && (
                    <div className={styles.cardWrapper}>
                      <PodcastCard
                        asset={content as any}
                        mode={mode}
                        onRemove={onRemove}
                        index={contentIdx}
                        active={contentIdx === contentChildIndex}
                        onClick={() => handleContentClick(index, contentIdx)}
                        lessonIndex={getLessonIndexForContent(contentIdx)}
                      >
                        {renderChildrenForContent?.(content)}
                      </PodcastCard>
                    </div>
                  )}
                  {(content.entityType === "simulation") && (
                    <div onClick={() => viewReport && onSimulationClick?.(content)}>
                      <div className={styles.cardWrapper}>
                        <SimulationCard
                          mode={mode}
                          asset={content as any}
                          onRemove={onRemove}
                          index={contentIdx}
                          active={contentIdx === contentChildIndex}
                          onClick={() => handleContentClick(index, contentIdx)}
                          lessonIndex={getLessonIndexForContent(contentIdx)}
                        >
                          {renderChildrenForContent?.(content)}
                        </SimulationCard>
                      </div>
                    </div>
                  )}
                  {dragIndicator?.index === contentIdx && !dragIndicator?.before &&
                    <DropIndicator key={`drop-indicator-${dragIndicator?.index}-after`} />
                  }
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
      {editMode &&
        <Button variant="secondary" className={styles.addContentButton}
          icon="paperflite-add"
          onClick={() => onAddLessonClicked?.(section.uuid)}
        >
          <span className={styles.text}>
            Add new content
            <div className={styles.options}>
              <div className={cx(styles.option, styles.default)}>
                <i className={cx("paperflite-up-arrow")}></i>
              </div>
            </div>

          </span>
        </Button>
      }

      {showDeleteSlider &&
        <Slider.Dialog
          width="300"
          show={showDeleteSlider}
          onClose={() => setShowDeleteSlider(false)}
        >
          <Slider.Header title="Deleting" />
          <Slider.Body>
            <p>Deleting this section will also delete lessons within it - consider relocating them using drag/drop
              feature into the course if you want to keep them
            </p>
          </Slider.Body>
          <Slider.Actions>
            <Button
              icon="paperflite-tick"
              text="Confirm"
              variant="primary"
              onClick={() => {
                onRemove?.(null, section.uuid);
                setShowDeleteSlider(false);
              }}
            />
            <Button
              text="Cancel"
              variant="secondary"
              onClick={() => setShowDeleteSlider(false)}
            />
          </Slider.Actions>
        </Slider.Dialog>
      }
    </div>
  );
};


interface ContentEmptyCardProps {
  index: number;
}

export const ContentEmptyCard: React.FC<ContentEmptyCardProps> = ({ index }) => {
  return (
    <div className={cx(styles.emptyCard, {
      [styles.first]: index === 0,
    })}>
      <div className={styles.icon}></div>
      <div className={styles.text}>
        <div className={styles.line} style={{ width: '8.5rem' }}></div>
        <div className={styles.line} style={{ width: '5.5rem' }}></div>
      </div>
    </div>
  );
}

export const ContentPlaceholderCard: React.FC = () => {
  return (
    <div className={cx(styles.emptyCard)}>
      <div className={styles.icon}></div>
      <div className={styles.text}>
        <div className={styles.line} style={{ width: '8.5rem' }}></div>
        <div className={styles.line} style={{ width: '5.5rem' }}></div>
      </div>
    </div>
  );
}

export const DropIndicator: React.FC = () => {
  const height = useSpring({
    from: { height: 0 },
    to: { height: 80 },
    config: { tension: 300, friction: 20 }
  });

  return (
    <animated.div style={height} className={styles.dropIndicator} />
  )
}

interface Podcast {
  id: string;
  uuid: string;
  name: string;
  description: string | null;
  entityType: "podcast";
  status?: "PUBLISHED" | "DRAFT" | string;
  order: number;
  parent: string | null;
  content: {
    id: string;
    name: string;
    entityType: "podcast";
    description: string;
    status: "PUBLISHED" | string;
  };
  contents: any[];
};


interface PodcastCardProps {
  asset: Podcast;
  index: number;
  active?: boolean;
  className?: string;
  onClick: (index: number) => void;
  children?: React.ReactNode;
  mode?: 'view' | 'edit';
  onRemove?: (parent: string | null, index: string) => void;
  lessonIndex?: number | string;
  readonly?: boolean;
}

export const PodcastCard: React.FC<PodcastCardProps> = ({
  asset,
  index,
  active,
  className,
  onClick,
  children,
  mode,
  onRemove,
  lessonIndex,
  readonly
}) => {
  const editMode = mode === 'edit';
  const title = _.get(asset, "name") || "Untitled Podcast";

  const fileType = useMemo(() => _.get(asset,"content.entityType","FILE"),[asset]);
  const audio = _.get(asset,"content.audio.duration",null)

  const duration = useMemo(() => {
    const finalDuration = audio ?? _.get(asset, "content.duration", null);
    if (!finalDuration) {
      return null;
    }
    return FileService.formatDuration(finalDuration);
  }, [asset]);

  const errors = useSelector((state: any) => {
    const errList = _.get(state.courses, "edit.errors", []) || [];
    const filtered = _.filter(errList, { type: "podcast", uuid: asset.uuid });
    return _.size(filtered);
  }, _.isEqual);

  const thumbnail = _.get(asset, "content.banner.background", null);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: asset!.uuid || 'new-podcast',
    disabled: readonly,
    data: {
      entity: asset,
      index,
      parent: asset.parent || null,
      type: 'podcast'
    }
  });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(asset.parent, asset.uuid);
    }
  }

  return (
    <div
      className={cx(styles.entity, styles.podcast, className, {
        [styles.readonly]: !editMode,
        [styles.active]: active,
        [styles.errors]: errors > 0,
        [styles.dragging]: isDragging,
      })}
      onClick={handleClick}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      {editMode &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }

      {(!asset.parent || mode === 'view') && (
        <div className={styles.index}>{lessonIndex} /</div>
      )}
      <div className={styles.entityDetail}>
        <Thumbnail
          className={styles.icon}
          media={thumbnail}
          defaultImage="/images/assets/podcastThumbnail.jpg"
        />
        <div className={styles.details}>
          <Text className={styles.name} text={title} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{fileType.toUpperCase()}</span>
            {duration && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{duration}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.right}>{children}</div>

      {editMode &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }

      {errors > 0 && (
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      )}
    </div>
  );
};

interface Simulation {
  id: string;
  uuid: string;
  name: string;
  description: string | null;
  entityType: "simulation";
  status?: "PUBLISHED" | "DRAFT" | string;
  order: number;
  parent: string | null;
  content: {
    id: string;
    name: string;
    entityType: "podcast";
    description: string;
    status: "PUBLISHED" | string;
  };
  contents: any[];
};
interface SimulationCardProps {
  asset: Simulation;
  index: number;
  active?: boolean;
  className?: string;
  score?: string | number | null;
  onClick: (index: number) => void;
  children?: React.ReactNode;
  mode?: 'view' | 'edit';
  onRemove?: (parent: string | null, index: string) => void;
  lessonIndex?: number | string;
  readonly?: boolean
}

export const SimulationCard: React.FC<SimulationCardProps> = ({
  asset,
  index,
  active,
  className,
  score,
  onClick,
  children,
  mode,
  onRemove,
  lessonIndex,
  readonly
}) => {

  const editMode = mode === 'edit';
  const title = _.get(asset, "name") || "Untitled Podcast";

  const fileType = useMemo(() => _.get(asset,"content.entityType","FILE"),[asset]);

  const duration = useMemo(() => {
    const finalDuration = _.get(asset, "content.duration", null);
    return finalDuration;
  }, [asset]);

  const timeBound = useMemo(() => {
    return _.get(asset, "content.timeBound", null);
  }, [asset]);

  const callType = useMemo(() => _.get(asset, "content.callType", ""), [asset]);

  const errors = useSelector((state: any) => {
    const errList = _.get(state.courses, "edit.errors", []) || [];
    const filtered = _.filter(errList, { type: "simulation", uuid: asset.uuid });
    return _.size(filtered);
  }, _.isEqual);

  const thumbnail = _.get(asset, "content.banner.background", null);

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: asset!.uuid || 'new-lesson',
    disabled: readonly,
    data: {
      entity: asset,
      index,
      parent: asset.parent || null,
      type: 'simulation'
    }
  });

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (editMode && e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  const handleRemove = (e) => {
    e.preventDefault();
    if (onRemove) {
      onRemove(asset.parent, asset.uuid);
    }
  }

  return (
    <div
      className={cx(styles.entity, styles.podcast, className, {
        [styles.readonly]: !editMode,
        [styles.active]: active,
        [styles.errors]: errors > 0,
        [styles.dragging]: isDragging,
      })}
      onClick={handleClick}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >

      {editMode &&
        <i className={cx(styles.dragHandle, "paperflite-drag")}></i>
      }

      {(!asset.parent || mode === 'view') && (
        <div className={styles.index}>{lessonIndex} /</div>
      )}
      <div className={styles.entityDetail}>
        <Thumbnail
          className={cx(styles.icon, {
            [styles.discovery]: callType === "discovery",
            [styles.coldcall]: callType === "cold_call",
            [styles.elevatorPitch]: callType === "elevator_pitch"
          })}
          media={thumbnail}
          defaultImage="/images/assets/simulationThumbnail.jpg"
        />
        <div className={styles.details}>
          <Text className={styles.name} text={title} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{fileType.toUpperCase()}</span>
            {duration !== 0 && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{callType === "elevator_pitch"
                  ? FileService.formatTimeBound(timeBound || 0, "long")
                  : `${duration} Minutes`}</span>
              </>
            )}
            {score && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>score: </span> <span className={styles.value}>{score}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.right}>{children}</div>

      {editMode &&
        <div className={styles.actions}>
          <button className={styles.action} onClick={handleRemove}>
            <i className={'paperflite-delete'}></i>
          </button>
        </div>
      }

      {errors > 0 && (
        <div className={styles.errorIndicator}>
          <span>{errors}</span>
          <i className="fa-solid fa-triangle-exclamation"></i>
        </div>
      )}
    </div>
  );
};
